import { useEffect, useState } from 'react'
import { Plus, Edit2, Trash2, X, Save, Briefcase } from 'lucide-react'
import api from '../../services/api'

const TRIGGER_OPTIONS = [
  { value: 'all', label: 'All incidents' },
  { value: 'notifiable', label: 'Notifiable incidents only' },
  { value: 'high_and_above', label: 'High severity and above' },
  { value: 'critical_only', label: 'Critical only' },
]

const METHOD_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone call' },
  { value: 'portal', label: 'Client portal' },
]

const WINDOW_OPTIONS = [
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
]

const EMPTY_FORM = {
  client_name: '',
  safety_contact_name: '',
  safety_contact_email: '',
  safety_contact_phone: '',
  notification_trigger: 'notifiable',
  notification_window_hours: 2,
  preferred_method: 'email',
  portal_url: '',
  required_info: [] as string[],
}

export default function ContractsSettings() {
  const [contracts, setContracts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [newReqItem, setNewReqItem] = useState('')

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/contracts')
      setContracts(data.data || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const openAdd = () => {
    setEditId(null)
    setForm({ ...EMPTY_FORM })
    setShowForm(true)
  }

  const openEdit = (contract: any) => {
    setEditId(contract.id)
    let requiredInfo: string[] = []
    try { if (contract.required_info) requiredInfo = JSON.parse(contract.required_info) } catch {}
    setForm({
      client_name: contract.client_name || '',
      safety_contact_name: contract.safety_contact_name || '',
      safety_contact_email: contract.safety_contact_email || '',
      safety_contact_phone: contract.safety_contact_phone || '',
      notification_trigger: contract.notification_trigger || 'notifiable',
      notification_window_hours: contract.notification_window_hours || 2,
      preferred_method: contract.preferred_method || 'email',
      portal_url: contract.portal_url || '',
      required_info: requiredInfo,
    })
    setShowForm(true)
  }

  const save = async () => {
    if (!form.client_name.trim()) return
    setSaving(true)
    try {
      if (editId) {
        await api.patch(`/contracts/${editId}`, form)
      } else {
        await api.post('/contracts', form)
      }
      setShowForm(false)
      setEditId(null)
      await load()
      showToast(editId ? 'Contract updated' : 'Contract added')
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Save failed')
    }
    setSaving(false)
  }

  const deleteContract = async (id: string) => {
    if (!confirm('Delete this client contract?')) return
    setDeleting(id)
    try {
      await api.delete(`/contracts/${id}`)
      await load()
      showToast('Contract deleted')
    } catch {}
    setDeleting(null)
  }

  const addRequiredItem = () => {
    if (!newReqItem.trim()) return
    setForm(f => ({ ...f, required_info: [...f.required_info, newReqItem.trim()] }))
    setNewReqItem('')
  }

  const removeRequiredItem = (i: number) => {
    setForm(f => ({ ...f, required_info: f.required_info.filter((_, idx) => idx !== i) }))
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>Client Contracts</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Manage client notification obligations for notifiable incidents
          </p>
        </div>
        <button type="button" onClick={openAdd} className="btn btn-primary flex items-center gap-2">
          <Plus size={15} /> Add Client
        </button>
      </div>

      {toast && (
        <div className="text-sm p-3 rounded-lg font-medium" style={{ background: 'rgba(0,135,90,0.1)', color: '#00875A' }}>
          {toast}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : contracts.length === 0 ? (
        <div className="card text-center py-12 space-y-3">
          <Briefcase size={40} style={{ color: 'var(--text-muted)', margin: '0 auto' }} />
          <p style={{ color: 'var(--text-muted)' }}>No client contracts configured</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Add client contracts to track notification obligations for notifiable incidents
          </p>
          <button type="button" onClick={openAdd} className="btn btn-primary">Add First Client</button>
        </div>
      ) : (
        <div className="space-y-4">
          {contracts.map(contract => {
            let requiredItems: string[] = []
            try { if (contract.required_info) requiredItems = JSON.parse(contract.required_info) } catch {}
            return (
              <div key={contract.id} className="card space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--text)' }}>{contract.client_name}</h3>
                    {contract.safety_contact_name && (
                      <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                        {contract.safety_contact_name}
                        {contract.safety_contact_email && ` · ${contract.safety_contact_email}`}
                        {contract.safety_contact_phone && ` · ${contract.safety_contact_phone}`}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button type="button" onClick={() => openEdit(contract)}
                      className="btn btn-secondary text-xs flex items-center gap-1">
                      <Edit2 size={12} /> Edit
                    </button>
                    <button type="button" onClick={() => deleteContract(contract.id)}
                      disabled={deleting === contract.id}
                      className="btn text-xs flex items-center gap-1"
                      style={{ background: 'rgba(222,53,11,0.1)', color: 'var(--red)', border: '1px solid var(--red)' }}>
                      <Trash2 size={12} /> {deleting === contract.id ? '...' : 'Delete'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span>Trigger: <strong style={{ color: 'var(--text)' }}>{TRIGGER_OPTIONS.find(o => o.value === contract.notification_trigger)?.label || contract.notification_trigger}</strong></span>
                  <span>Notify within: <strong style={{ color: 'var(--text)' }}>{contract.notification_window_hours}h</strong></span>
                  <span>Method: <strong style={{ color: 'var(--text)' }}>{contract.preferred_method}</strong></span>
                </div>
                {requiredItems.length > 0 && (
                  <div className="text-xs space-y-0.5" style={{ color: 'var(--text-muted)' }}>
                    <span className="font-semibold">Required items: </span>
                    {requiredItems.map((item, i) => <span key={i}>{item}{i < requiredItems.length - 1 ? ', ' : ''}</span>)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Add/Edit Form Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h3 className="font-bold" style={{ color: 'var(--text)' }}>
                {editId ? 'Edit Client Contract' : 'Add Client Contract'}
              </h3>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                  Client Name *
                </label>
                <input
                  value={form.client_name}
                  onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                  placeholder="e.g. Acme Corporation"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Safety Contact Name</label>
                  <input value={form.safety_contact_name} onChange={e => setForm(f => ({ ...f, safety_contact_name: e.target.value }))} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Safety Contact Phone</label>
                  <input value={form.safety_contact_phone} onChange={e => setForm(f => ({ ...f, safety_contact_phone: e.target.value }))} placeholder="0400 000 000" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Safety Contact Email</label>
                <input type="email" value={form.safety_contact_email} onChange={e => setForm(f => ({ ...f, safety_contact_email: e.target.value }))} placeholder="safety@client.com" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Notification Trigger</label>
                  <select value={form.notification_trigger} onChange={e => setForm(f => ({ ...f, notification_trigger: e.target.value }))}>
                    {TRIGGER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Notify Within</label>
                  <select value={form.notification_window_hours} onChange={e => setForm(f => ({ ...f, notification_window_hours: Number(e.target.value) }))}>
                    {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Preferred Notification Method</label>
                <div className="flex gap-4">
                  {METHOD_OPTIONS.map(o => (
                    <label key={o.value} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="method"
                        value={o.value}
                        checked={form.preferred_method === o.value}
                        onChange={() => setForm(f => ({ ...f, preferred_method: o.value }))}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>

              {form.preferred_method === 'portal' && (
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Portal URL</label>
                  <input type="url" value={form.portal_url} onChange={e => setForm(f => ({ ...f, portal_url: e.target.value }))} placeholder="https://client-portal.example.com" />
                </div>
              )}

              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Required Information to Include</label>
                <div className="flex gap-2 mb-2">
                  <input
                    value={newReqItem}
                    onChange={e => setNewReqItem(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRequiredItem()}
                    placeholder="e.g. Incident reference number"
                    className="flex-1"
                  />
                  <button type="button" onClick={addRequiredItem} className="btn btn-secondary text-sm">Add</button>
                </div>
                {form.required_info.length > 0 && (
                  <div className="space-y-1">
                    {form.required_info.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg"
                        style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                        <span className="flex-1" style={{ color: 'var(--text)' }}>{item}</span>
                        <button type="button" onClick={() => removeRequiredItem(i)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <button
                type="button"
                onClick={save}
                disabled={saving || !form.client_name.trim()}
                className="btn btn-primary flex items-center gap-2"
              >
                <Save size={14} /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
